import { DOCUMENT, Injectable, inject } from '@angular/core';
import { LocalService } from '@app/core/services/local.service';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment-timezone';
// Import the locales you want to support (imortant for moment to work correctly with different languages)
import 'moment/locale/de-ch';
import 'moment/locale/fr-ch';
import 'moment/locale/it-ch';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private document = inject<Document>(DOCUMENT);
  private translateService = inject(TranslateService);
  private localService = inject(LocalService);

  private LOCALSTORAGE_KEY = 'lang';

  init(): void {
    this.translateService.addLangs(['de-CH', 'fr-CH', 'it-CH']);
    const currentLang = this.determineCurrentLang();
    this.translateService.setFallbackLang('de-CH');
    void this.changeLocale(currentLang);
  }

  async changeLocale(newLocale: string): Promise<void> {
    const locale = this.parseLang(newLocale) ?? 'de-CH';
    moment.locale(locale);
    this.localService.localStorageSet(this.LOCALSTORAGE_KEY, locale);
    this.document.documentElement.lang = locale;
    await firstValueFrom(this.translateService.use(locale));
  }

  determineCurrentLang(): string {
    let userLang = this.localService.localStorageGet(this.LOCALSTORAGE_KEY);
    if (userLang != null) {
      userLang = this.parseLang(userLang);
    }
    if (userLang == null) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
      userLang = this.parseLang(navigator.language || ((navigator as any).userLanguage as string));
    }
    if (userLang != null) {
      return userLang;
    } else {
      return 'de-CH';
    }
  }

  private parseLang(userLang: string): string | null {
    if (userLang) {
      const langLower = userLang.toLowerCase();
      if (this.isLang(langLower, 'fr')) {
        return 'fr-CH';
      } else if (this.isLang(langLower, 'de')) {
        return 'de-CH';
      } else if (this.isLang(langLower, 'it')) {
        return 'it-CH';
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  private isLang(langLower: string, lang: string): boolean {
    return langLower === lang || langLower.startsWith(lang + '_') || langLower.startsWith(lang + '-');
  }

  public sortTranslated(a: string, b: string): number {
    return (this.translateService.instant(a) as string).localeCompare(this.translateService.instant(b) as string);
  }
}
